import {
  cloneBoatDocument,
  createSkiffBoat,
  SKIFF_BOAT_ID,
  type BoatDocument,
} from '../sim/boat/index.ts';
import { cloneMapDocument, validateMap, type MapDocument } from '../sim/map/index.ts';

export type TestOrigin = 'editor' | 'workshop';

/** Synthetic row when the boat catalog is empty. A real `skiff` file wins when it exists. */
export const EMPTY_CATALOG_SKIFF_ID = SKIFF_BOAT_ID;

export type TestChoice = {
  id: string;
  label: string;
  disabled: boolean;
};

export type TestPlayHandoff = {
  map: MapDocument;
  boat: BoatDocument;
  testPlay: true;
  testOrigin: TestOrigin;
  mapId?: string;
  repoFile?: string;
  boatFile?: string;
  /** Workshop serialize of the last real save, so a test return stays dirty. */
  boatBaseline?: string;
};

export type TestPlayReturn =
  | { scene: 'EditorScene'; data: { map: MapDocument } }
  | {
      scene: 'BoatScene';
      data: { boat: BoatDocument; fileName?: string; baselineJson?: string };
    };

export function handoffFromEditor(
  map: MapDocument,
  boat: BoatDocument,
  repoFile: string | null,
): TestPlayHandoff {
  return {
    map: cloneMapDocument(map),
    boat: cloneBoatDocument(boat),
    testPlay: true,
    testOrigin: 'editor',
    ...(repoFile ? { repoFile } : {}),
  };
}

export function handoffFromWorkshop(
  boat: BoatDocument,
  entry: { id: string; map: MapDocument },
  boatFile: string | null,
  boatBaseline: string | null,
): TestPlayHandoff {
  return {
    map: cloneMapDocument(entry.map),
    boat: cloneBoatDocument(boat),
    testPlay: true,
    testOrigin: 'workshop',
    mapId: entry.id,
    repoFile: `${entry.id}.json`,
    ...(boatFile ? { boatFile } : {}),
    ...(boatBaseline !== null ? { boatBaseline } : {}),
  };
}

export function boatTestChoices(
  entries: readonly { id: string; label: string }[],
): TestChoice[] {
  if (entries.length === 0) {
    return [{ id: EMPTY_CATALOG_SKIFF_ID, label: 'Skiff', disabled: false }];
  }

  return entries.map((entry) => ({
    id: entry.id,
    label: entry.label,
    disabled: false,
  }));
}

export function mapTestChoices(
  entries: readonly { id: string; label: string; map: MapDocument }[],
): TestChoice[] {
  return entries.map((entry) => ({
    id: entry.id,
    label: entry.label,
    disabled: !validateMap(entry.map).playable,
  }));
}

export function resolveTestBoat(
  id: string,
  entries: readonly { id: string; boat: BoatDocument }[],
): BoatDocument | null {
  const found = entries.find((entry) => entry.id === id);

  if (found) {
    return cloneBoatDocument(found.boat);
  }

  if (entries.length === 0 && id === EMPTY_CATALOG_SKIFF_ID) {
    return createSkiffBoat();
  }

  return null;
}

export function testLeaveLabel(origin: TestOrigin): string {
  return origin === 'workshop' ? 'Back to workshop' : 'Back to editor';
}

export function testPlayReturn(
  origin: TestOrigin,
  map: MapDocument,
  boat: BoatDocument,
  boatFile?: string | null,
  boatBaseline?: string | null,
): TestPlayReturn {
  if (origin === 'workshop') {
    return {
      scene: 'BoatScene',
      data: {
        boat: cloneBoatDocument(boat),
        ...(boatFile ? { fileName: boatFile } : {}),
        ...(boatBaseline != null ? { baselineJson: boatBaseline } : {}),
      },
    };
  }

  return {
    scene: 'EditorScene',
    data: { map: cloneMapDocument(map) },
  };
}

/** Empty is 0. Anything that is not a finite speed of at least 0 stays unparsed. */
export function parseStartingSpeedKmh(raw: string): number | null {
  const trimmed = raw.trim();

  if (trimmed === '') {
    return 0;
  }

  const value = Number(trimmed);

  if (!Number.isFinite(value) || value < 0) {
    return null;
  }

  return value;
}
